import {Component, OnDestroy, OnInit} from '@angular/core';
import {StateService} from '../../core/services/state.service';
import {ActionBarService} from './action-bar.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {filter, take} from 'rxjs/operators';
import {Title} from '@angular/platform-browser';
import {Subscription} from "rxjs";

@Component({
  selector: 'mes-web-action-bar',
  templateUrl: './action-bar.component.html'
})
export class ActionBarComponent implements OnInit, OnDestroy {
  breadCrumbs: Array<BreadCrumb>;
  searchString: string;
  private lastSearchOptions: { enabled?: boolean, advanced?: boolean };
  private subscription: Subscription;
  private searchSubscription: Subscription;
  enabled: boolean = true;

  constructor(public stateService: StateService, public actionBarService: ActionBarService, private router: Router, private activatedRoute: ActivatedRoute, private titleService: Title) {
    this.subscription = this.router.events.pipe(
      filter(value => value instanceof NavigationEnd)
    ).subscribe(res => {
      this.searchString = null;
      this.actionBarService.advancedFilter$.next(false);
      this.actionBarService.searchOptions = null;
      this.actionBarService.search$.next(this.searchString);
      if (!this.router.getCurrentNavigation().extras?.state?.skipReset) {
        this.actionBarService.triggerChanges();
      }
      this.breadCrumbs = this.buildBreadCrumb(this.activatedRoute);
      this.titleService.setTitle(this.breadCrumbs.map(value => value.label).join(' / '));
      this.actionBarService.searchOptions = this.lastSearchOptions || {
        advanced: false,
        enabled: false
      };
      this.actionBarService.isForceSaveShown$.next(false);
    });
  }

  ngOnInit() {
    this.searchSubscription = this.actionBarService.search$.subscribe(value => {
      this.searchString = value;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.searchSubscription.unsubscribe();
  }

  toggleAdvanced(): void {
    this.actionBarService.advancedFilter$.pipe(take(1)).subscribe(state => {
      this.actionBarService.advancedFilter$.next(!state);
    });
  }

  searchChanged($event) {
    this.actionBarService.search$.next($event.toLowerCase());
  }

  buildBreadCrumb(route: ActivatedRoute, url: string = '', breadcrumbs: Array<BreadCrumb> = []): Array<BreadCrumb> {
    // If no routeConfig is available we are on the root path

    const path = route.routeConfig ? route.routeConfig.path : '';
    // In the routeConfig the complete path is not available,
    // so we rebuild it each time
    const nextUrl = `${url}${path}/`;

    if (route.routeConfig && route.routeConfig.data) {
      this.lastSearchOptions = route.routeConfig.data['search'];
      this.enabled = route.routeConfig.data.actionbar ?? true;

      const label = route.routeConfig && route.routeConfig.data ? route.routeConfig.data['breadcrumb'] : undefined;
      const breadcrumb: BreadCrumb = {
        label: label,
        url: nextUrl
      };

      route.params.subscribe(value => {
        for (const paramsKey of Object.keys(value)) {
          breadcrumb.url = breadcrumb.url.replace(':' + paramsKey, '');
        }
        breadcrumb.params = Object.keys(value).map(key => value[key]);
      });

      breadcrumb.url = breadcrumb.url.replace(/\/*$/, '');

      if (breadcrumb.label) {
        breadcrumbs.push(breadcrumb);
      }
    }

    if (route.firstChild) {
      // If we are not on our current path yet,
      // there will be more children to look after, to build our breadcumb
      return this.buildBreadCrumb(route.firstChild, nextUrl, breadcrumbs);
    }
    return breadcrumbs;
  }

  getRouteParams(breadcrumb: BreadCrumb) {
    const params = [breadcrumb.url];
    if (breadcrumb.params.length) {
      return [...params, breadcrumb.params.join('/')];
    }
    return params;
  }
}

export interface BreadCrumb {
  label: string;
  url: string;
  params?: any[];
}
